/**
 * Revoke / restore cart-drawer upsell popup discounts when trigger conditions change.
 */

import {
  CHECKOUT_TRACKING_KEYS,
  buildStorefrontCartLineProperties,
  clearStorefrontPopupTrackingOnProperties,
  getLineCartDrawerUpsellPopupId,
  getLineStorefrontPopupId,
  normalizeLineItemPropertiesObject,
  resolveTrackingPopupId,
} from "./checkout-tracking.js";
import { shouldShowCartDrawerUpsellBlock } from "./cart-drawer-upsell-conditions.js";

export function getPopupTrackedCartLines(cart, popup) {
  const popupId = resolveTrackingPopupId(popup);
  if (!popupId) return [];

  return (Array.isArray(cart?.items) ? cart.items : []).filter((item) => {
    const linePopupId = getLineStorefrontPopupId(item?.properties);
    return linePopupId && String(linePopupId) === popupId;
  });
}

export function getCartDrawerUpsellRestoreLines(cart, popup) {
  const popupId = resolveTrackingPopupId(popup);
  if (!popupId) return [];

  return (Array.isArray(cart?.items) ? cart.items : []).filter((item) => {
    const originPopupId = getLineCartDrawerUpsellPopupId(item?.properties);
    const activePopupId = getLineStorefrontPopupId(item?.properties);
    return (
      originPopupId &&
      String(originPopupId) === popupId &&
      !activePopupId
    );
  });
}

export function stripStorefrontPopupTrackingProperties(properties) {
  return clearStorefrontPopupTrackingOnProperties(properties);
}

export function cartLinesNeedUpsellDiscountRevocation(popup, cart) {
  if (!popup || !cart) return false;
  if (shouldShowCartDrawerUpsellBlock(popup, { cart })) return false;
  return getPopupTrackedCartLines(cart, popup).length > 0;
}

export function cartLinesNeedUpsellDiscountRestore(popup, cart) {
  if (!popup || !cart) return false;
  if (!shouldShowCartDrawerUpsellBlock(popup, { cart })) return false;
  return getCartDrawerUpsellRestoreLines(cart, popup).length > 0;
}

/** True when trigger change requires clearing or re-applying upsell discounts. */
export function cartDrawerUpsellDiscountNeedsSync(popup, cart) {
  return (
    cartLinesNeedUpsellDiscountRevocation(popup, cart) ||
    cartLinesNeedUpsellDiscountRestore(popup, cart)
  );
}

export function buildCartUpsellDiscountSyncFingerprint(cart, popup) {
  const popupId = resolveTrackingPopupId(popup);
  const items = (Array.isArray(cart?.items) ? cart.items : []).map((item) => ({
    key: item?.key || item?.id || "",
    quantity: Number(item?.quantity) || 0,
    popupId: getLineStorefrontPopupId(item?.properties),
    upsellOrigin: getLineCartDrawerUpsellPopupId(item?.properties),
  }));
  return JSON.stringify({ popupId, items });
}

function isCartPayload(value) {
  return Boolean(value && typeof value === "object" && Array.isArray(value.items));
}

export function buildRestoreStorefrontPopupTrackingProperties(
  line,
  popup,
  shop = ""
) {
  const popupId = resolveTrackingPopupId(popup);
  const trackingProps =
    buildStorefrontCartLineProperties({
      shop,
      popup,
      product: { id: line?.product_id },
      variant: { id: line?.variant_id },
      quantity: line?.quantity,
      slotNo: "cart-drawer-upsell-restore",
      cartDrawerUpsell: true,
      offerRuleId:
        line?.properties?.["_weupsell_offerRuleId"] ||
        popup?.__weupsellMatchedOfferRuleId ||
        "default",
    }) || {};

  return {
    ...normalizeLineItemPropertiesObject(line?.properties),
    ...trackingProps,
    ...(popupId
      ? { [CHECKOUT_TRACKING_KEYS.CART_DRAWER_UPSELL_LINE]: popupId }
      : {}),
  };
}

async function syncCartDrawerUpsellDiscountLines({
  popup,
  cart,
  changeLineItem,
  selectLines,
  mapProperties,
  shop = "",
}) {
  if (!popup || !cart || typeof changeLineItem !== "function") return null;

  let workingCart = cart;
  let lines = selectLines(workingCart, popup);
  if (!lines.length) return null;

  let changed = false;
  for (const line of lines) {
    const lineKey = line?.key || line?.id;
    if (!lineKey) continue;

    const nextProperties = mapProperties(line, popup, shop);
    const updated = await changeLineItem({
      id: lineKey,
      quantity: Number(line.quantity) || 1,
      properties: nextProperties,
    });

    if (isCartPayload(updated)) {
      workingCart = updated;
      lines = selectLines(workingCart, popup);
      changed = true;
      continue;
    }

    changed = true;
  }

  return changed ? workingCart : null;
}

/**
 * @returns {Promise<object | null>}
 */
export async function revokeCartDrawerUpsellDiscounts({
  popup,
  cart,
  changeLineItem,
}) {
  if (shouldShowCartDrawerUpsellBlock(popup, { cart })) return null;

  return syncCartDrawerUpsellDiscountLines({
    popup,
    cart,
    changeLineItem,
    selectLines: getPopupTrackedCartLines,
    mapProperties: (line) =>
      clearStorefrontPopupTrackingOnProperties(line.properties),
  });
}

/**
 * @returns {Promise<object | null>}
 */
export async function restoreCartDrawerUpsellDiscounts({
  popup,
  cart,
  changeLineItem,
  shop = "",
}) {
  if (!shouldShowCartDrawerUpsellBlock(popup, { cart })) return null;

  return syncCartDrawerUpsellDiscountLines({
    popup,
    cart,
    changeLineItem,
    selectLines: getCartDrawerUpsellRestoreLines,
    mapProperties: (line, activePopup, activeShop) =>
      buildRestoreStorefrontPopupTrackingProperties(
        line,
        activePopup,
        activeShop
      ),
    shop,
  });
}

/**
 * Single entry: clear discount when triggers fail; restore when triggers pass
 * and upsell product is still in cart (cart-drawer upsell lines only).
 *
 * @returns {Promise<object | null>}
 */
export async function syncCartDrawerUpsellDiscount({
  popup,
  cart,
  changeLineItem,
  shop = "",
}) {
  if (!popup || !cart || typeof changeLineItem !== "function") return null;

  if (cartLinesNeedUpsellDiscountRevocation(popup, cart)) {
    return revokeCartDrawerUpsellDiscounts({ popup, cart, changeLineItem });
  }

  if (cartLinesNeedUpsellDiscountRestore(popup, cart)) {
    return restoreCartDrawerUpsellDiscounts({
      popup,
      cart,
      changeLineItem,
      shop,
    });
  }

  return null;
}
